"use client";

import { useMemo } from "react";
import Link from "next/link";
import type { CJobOffersArticle as CJobOffersArticleData } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { cJobOffersTranslations } from "./CJobOffers.i18n";
import styles from "./CJobOffers.module.css";

export type CJobOffersArticleProps = {
  article: CJobOffersArticleData;
  /** URL powrotu do listy z backendu (fallback dla `useCatalogLocation.buildPageHref(1)`). */
  listHref: string | null;
  hideDate: boolean;
};

/**
 * Widok pojedynczej oferty pracy (article view). Wyodrębniony z `CJobOffers.tsx`.
 *
 * **Back link:** primary source `useCatalogLocation.buildPageHref(1)` (client-side,
 * derived from current URL). Prev iteracja polegala na `listHref` z backendu, ktory
 * potrafil generowac URL wskazujacy na ten sam artykul -> "link nic nie robi".
 *
 * **Apply button guard:** gdy komponent `m_polls` nie jest zainstalowany na stronie,
 * backend `buildArticleView` generuje `apply_href` wewnatrz katalogu ofert
 * (`getById($this->component->id_catalog ?? $this->component->id)` zwraca sam katalog
 * ofert). Klikniecie takiego linku wraca do listy zamiast otworzyc ankiete. Guard:
 * jesli `apply_href` prowadzi do tego samego katalogu co lista -> nie renderujemy
 * przycisku.
 */
export function CJobOffersArticle({
  article,
  listHref,
  hideDate,
}: CJobOffersArticleProps) {
  const t = useT(cJobOffersTranslations);
  const loc = useCatalogLocation();

  const mainImage = useMemo(
    () => article.images.find((img) => img.is_main) ?? article.images[0] ?? null,
    [article.images],
  );
  const galleryImages = useMemo(
    () => article.images.filter((img) => img !== mainImage),
    [article.images, mainImage],
  );

  /* Czysty URL katalogu (bez extras/actions/pages/articles). `buildPageHref(1)`
     nie nadaje sie tutaj - z designu zachowuje `articles` z biezacego URL
     (paginacja w kontekscie artykulu), wiec zwrocilby ten sam URL na ktorym
     jestesmy -> "klikniecie nic nie robi". */
  const clientBackHref = "/" + [loc.locale, ...loc.pathSegments, ""].join("/");
  const serverBackHref = listHref ? extractInternalHref(listHref) : null;
  const backHref = clientBackHref || serverBackHref;

  /* `apply_is_external` = flaga z backendu: `true` gdy `apply_href` pochodzi z `url_add`
     (arbitralny URL admina, moze wskazywac na dowolna domene) - wtedy renderujemy `<a>`
     bez tykania URLa. Dla `id_poll` (Imperial-generated internal URL) idziemy przez
     `extractInternalHref` + `<Link>`. Bez tej flagi `extractInternalHref` reducowal
     `https://external.com/apply` do `/apply` i `<Link>` szedl na strone glowna. */
  const applyPath = !article.apply_is_external && article.apply_href
    ? extractInternalHref(article.apply_href)
    : null;
  const applyIsMisrouted =
    applyPath !== null &&
    (applyPath === clientBackHref || applyPath.startsWith(clientBackHref));
  const showApply = !!article.apply_href && !applyIsMisrouted;
  const applyIsExternal =
    showApply && (article.apply_is_external || applyPath === null);

  return (
    <article className={styles.article}>
      {backHref && (
        <div className={styles.articleBack}>
          <Link href={backHref} className={styles.articleBackLink}>
            {t.backToList}
          </Link>
        </div>
      )}

      <header className={styles.articleHeader}>
        <h2 className={styles.articleTitle}>{article.name}</h2>
        <div className={styles.articleMeta}>
          {!hideDate && article.publication_date && (
            <time
              className={styles.cardMetaItem}
              dateTime={article.publication_date}
            >
              {t.formatDate(article.publication_date)}
            </time>
          )}
          {article.category_label && (
            <span className={styles.cardMetaItem}>{article.category_label}</span>
          )}
          {(article.city || article.province) && (
            <span className={styles.cardMetaItem}>
              {[article.city, article.province].filter(Boolean).join(", ")}
            </span>
          )}
          {article.reference_number && (
            <span className={styles.cardMetaItem}>
              {t.referenceNumber}: {article.reference_number}
            </span>
          )}
        </div>
      </header>

      {mainImage && (
        <figure className={styles.articleMainImage}>
          <img
            src={mainImage.full_src}
            alt={mainImage.description || article.name}
            width={mainImage.width ?? undefined}
            height={mainImage.height ?? undefined}
            loading="eager"
          />
        </figure>
      )}

      {article.tasks && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>{t.tasksHeading}</h3>
          <div
            className={styles.sectionBody}
            dangerouslySetInnerHTML={{ __html: article.tasks }}
          />
        </section>
      )}

      {article.expectations && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>{t.expectationsHeading}</h3>
          <div
            className={styles.sectionBody}
            dangerouslySetInnerHTML={{ __html: article.expectations }}
          />
        </section>
      )}

      {article.offer && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>{t.offerHeading}</h3>
          <div
            className={styles.sectionBody}
            dangerouslySetInnerHTML={{ __html: article.offer }}
          />
        </section>
      )}

      {article.paragraphs && article.paragraphs.length > 0 && (
        <ParagraphRenderer paragraphs={article.paragraphs} />
      )}

      {galleryImages.length > 0 && (
        <div className={styles.gallery}>
          {galleryImages.map((img) => (
            <a
              key={img.name}
              href={img.full_src}
              className={styles.galleryItem}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={img.thumb_src}
                alt={img.description || article.name}
                width={img.width ?? undefined}
                height={img.height ?? undefined}
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}

      {article.files.length > 0 && (
        <div className={styles.files}>
          <div className={styles.filesHeading}>{t.attachments}</div>
          <ul className={styles.filesList}>
            {article.files.map((file) => (
              <li key={file.name}>
                <a
                  href={file.href}
                  className={styles.fileLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <span>
                    {file.description || file.name}
                    {file.ext ? ` (.${file.ext})` : ""}
                  </span>
                  {file.size_bytes > 0 && (
                    <span className={styles.fileSize}>
                      {t.formatFileSize(file.size_bytes)}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showApply && (
        <div className={styles.applyWrap}>
          {applyIsExternal ? (
            <a
              href={article.apply_href!}
              className={styles.applyButton}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.applyButton}
            </a>
          ) : (
            <Link href={applyPath!} className={styles.applyButton}>
              {t.applyButton}
            </Link>
          )}
        </div>
      )}
    </article>
  );
}
